import { createReducer } from "typesafe-actions";

import { Activity, Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { RawFile } from "@mapmycustomers/shared/types/File";

import AssociationsState from "@app/store/associations/AssociationsState";
import { DEFAULT_ASSOCIATIONS_STATE } from "@app/store/associations/const";
import FileListItem from "@app/types/FileListItem";

import {
  Actions,
  changeAssociatedEntities,
  clearActivity,
  clearAllUploadedCreateEditActivityFiles,
  clearAssociations,
  createActivity,
  deleteActivity,
  fetchFilePreview,
  hideGlobalCreateActivityModal,
  initializeActivityModal,
  initializeEditActivityModal,
  relateUnrelateEntities,
  removeCreateEditActivityFile,
  showGlobalCreateActivityModal,
  updateActivity,
  updateActivityModalAssociations,
  uploadCreateEditActivityFiles,
} from "./actions";

export interface CreateEditActivityModalState {
  activityCompleted: boolean;
  activityTypes: ActivityType[];
  associatedFiles: RawFile[];
  associations: AssociationsState;
  cloning: boolean;
  createLoading: boolean;
  deleteLoading: boolean;
  editedActivity: Activity | undefined;
  entityRelating: boolean;
  entityRelatingSuccess: boolean;
  fileGroupId: string;
  filePreview?: Blob;
  filePreviewId?: RawFile["id"];
  filePreviewLoading: boolean;
  filesAdded: FileListItem[];
  filesDeleted: RawFile[];
  filesUploading: boolean;
  initializeLoading: boolean;
  isWarningDismissed: boolean;
  newAssociations?: {
    deal?: { accountId?: Company["id"]; contactId?: Person["id"] };
    personCompanyId?: Company["id"];
  };
  noAccess?: boolean;
  prefilledValues: Partial<Omit<Activity, "id">> | undefined;
  updateLoading: boolean;
  visible: boolean;
}

const initialState: CreateEditActivityModalState = {
  activityCompleted: false,
  activityTypes: [],
  associatedFiles: [],
  associations: DEFAULT_ASSOCIATIONS_STATE,
  cloning: false,
  createLoading: false,
  deleteLoading: false,
  editedActivity: undefined,
  entityRelating: false,
  entityRelatingSuccess: false,
  fileGroupId: "",
  filePreviewLoading: false,
  filesAdded: [],
  filesDeleted: [],
  filesUploading: false,
  initializeLoading: false,
  isWarningDismissed: false,
  newAssociations: undefined,
  prefilledValues: undefined,
  updateLoading: false,
  visible: false,
};

const createEditActivityModal = createReducer<CreateEditActivityModalState, Actions>(initialState)
  .handleAction(showGlobalCreateActivityModal, (state, { payload }) => ({
    ...state,
    associations: DEFAULT_ASSOCIATIONS_STATE,
    cloning: payload?.cloning ?? false,
    editedActivity: undefined,
    newAssociations: undefined,
    prefilledValues: payload?.activity,
    visible: true,
  }))
  .handleAction(hideGlobalCreateActivityModal, (state) => ({
    ...state,
    cloning: false,
    prefilledValues: undefined,
    visible: false,
  }))
  .handleAction(initializeActivityModal.request, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      associatedCompany: payload.fixedCompany,
      associatedDeal: payload.fixedDeal,
      associatedPerson: payload.fixedPerson,
      inaccessibleEntity: false,
    },
    entityRelatingSuccess: false,
    initializeLoading: true,
  }))
  .handleAction(initializeEditActivityModal.request, (state) => ({
    ...state,
    initializeLoading: true,
  }))
  .handleAction(initializeActivityModal.success, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      ...payload.associationsState,
    },
    fileGroupId: payload.fileGroupId,
    initializeLoading: false,
  }))
  .handleAction(updateActivityModalAssociations.request, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      associatedCompany: payload.fixedCompany,
      associatedDeal: payload.fixedDeal,
      associatedPerson: payload.fixedPerson,
    },
  }))
  .handleAction(updateActivityModalAssociations.success, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      ...payload.associationsState,
    },
  }))
  .handleAction(initializeEditActivityModal.success, (state, { payload }) => ({
    ...state,
    activityCompleted: payload.activityCompleted,
    associatedFiles: payload.files,
    associations: {
      ...state.associations,
      inaccessibleEntity:
        !!payload.activity &&
        !!payload.activity.assignee?.id &&
        (payload.activity.account?.userIds?.includes(payload.activity.assignee?.id) === false ||
          payload.activity.contact?.userIds?.includes(payload.activity.assignee?.id) === false ||
          payload.activity.deal?.userIds?.includes(payload.activity.assignee?.id) === false),
    },
    editedActivity: payload.activity,
    fileGroupId: payload.fileGroupId,
    noAccess: false,
    // not setting initializeLoading to false, because initializeEditActivityModal calls initializeActivityModal
    // immediately, so it doesn't make sense to set it to false because it will be set to true again
  }))
  .handleAction(
    [initializeActivityModal.failure, initializeEditActivityModal.failure],
    (state, { payload }) => ({
      ...state,
      initializeLoading: false,
      noAccess: !!payload,
    })
  )
  .handleAction(createActivity.request, (state) => ({
    ...state,
    createLoading: true,
  }))
  .handleAction([createActivity.success, createActivity.failure], (state) => ({
    ...state,
    createLoading: false,
    newAssociations: undefined,
  }))
  .handleAction(updateActivity.request, (state) => ({
    ...state,
    updateLoading: true,
  }))
  .handleAction([updateActivity.success, updateActivity.failure], (state) => ({
    ...state,
    newAssociations: undefined,
    updateLoading: false,
  }))
  .handleAction(deleteActivity.request, (state) => ({
    ...state,
    deleteLoading: true,
  }))
  .handleAction([deleteActivity.success, deleteActivity.failure], (state) => ({
    ...state,
    deleteLoading: false,
  }))
  .handleAction(changeAssociatedEntities.request, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      associatedCompany:
        "company" in payload ? payload.company : state.associations.associatedCompany,
      associatedDeal: "deal" in payload ? payload.deal : state.associations.associatedDeal,
      associatedPerson: "person" in payload ? payload.person : state.associations.associatedPerson,
      availableLoading: true,
    },
    entityRelatingSuccess: false,
  }))
  .handleAction(changeAssociatedEntities.success, (state, { payload }) => ({
    ...state,
    associations: { ...state.associations, ...payload, availableLoading: false },
  }))
  .handleAction(changeAssociatedEntities.failure, (state) => ({
    ...state,
    associations: { ...state.associations, availableLoading: false },
  }))
  .handleAction(clearActivity, (state) => ({
    ...state,
    editedActivity: undefined,
  }))
  .handleAction(clearAssociations, (state, { payload }) => ({
    ...state,
    associations: {
      ...state.associations,
      associatedCompany:
        (payload?.[EntityType.COMPANY] ?? true) ? undefined : state.associations.associatedCompany,
      associatedDeal:
        (payload?.[EntityType.DEAL] ?? true) ? undefined : state.associations.associatedDeal,
      associatedPerson:
        (payload?.[EntityType.PERSON] ?? true) ? undefined : state.associations.associatedPerson,
      inaccessibleEntity: false,
    },
  }))
  .handleAction(uploadCreateEditActivityFiles.request, (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleAction(uploadCreateEditActivityFiles.success, (state, action) => ({
    ...state,
    filesAdded: [...state.filesAdded, ...action.payload],
    filesUploading: false,
  }))
  .handleAction(uploadCreateEditActivityFiles.failure, (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleAction(removeCreateEditActivityFile.success, (state, action) => {
    const associatedFile = state.associatedFiles.find((file) => file.id === action.payload.id);
    return {
      ...state,
      // Removing the deleted file from store
      associatedFiles: state.associatedFiles.filter((file) => file.id !== action.payload.id),
      filesAdded: state.filesAdded.filter((file) => file.uploadedFile?.id !== action.payload.id),
      filesDeleted: associatedFile ? [...state.filesDeleted, action.payload] : state.filesDeleted,
    };
  })
  .handleAction(clearAllUploadedCreateEditActivityFiles, (state) => ({
    ...state,
    associatedFiles: [],
    filesAdded: [],
    filesDeleted: [],
    filesUploading: false,
  }))
  .handleAction(fetchFilePreview.request, (state, action) => ({
    ...state,
    filePreviewId: action.payload,
    filePreviewLoading: true,
  }))
  .handleAction(fetchFilePreview.success, (state, action) => ({
    ...state,
    filePreview: action.payload,
    filePreviewLoading: false,
  }))
  .handleAction(fetchFilePreview.failure, (state) => ({ ...state, filePreviewLoading: false }))
  .handleAction(
    relateUnrelateEntities.request,
    (
      state,
      {
        payload: {
          associatedCompany,
          associatedDeal,
          associatedPerson,
          isDealCorrectlyRelatedToCompany,
          isDealCorrectlyRelatedToPerson,
          isPersonCorrectlyRelatedToCompany,
          unrelate,
        },
      }
    ) => ({
      ...state,
      entityRelating: true,
      newAssociations: unrelate
        ? state.newAssociations
        : {
            deal:
              associatedDeal &&
              (!isDealCorrectlyRelatedToCompany || !isDealCorrectlyRelatedToPerson)
                ? {
                    accountId: !isDealCorrectlyRelatedToCompany ? associatedCompany?.id : undefined,
                    contactId: !isDealCorrectlyRelatedToPerson ? associatedPerson?.id : undefined,
                  }
                : undefined,
            personCompanyId:
              !isPersonCorrectlyRelatedToCompany && associatedCompany
                ? associatedCompany?.id
                : undefined,
          },
    })
  )
  .handleAction(
    relateUnrelateEntities.success,
    (state, { payload: { associationsState, deal, person, unrelate } }) => ({
      ...state,
      associations: {
        ...state.associations,
        ...associationsState,
        associatedDeal: deal ?? state.associations.associatedDeal,
        associatedPerson: person ?? state.associations.associatedPerson,
      },
      entityRelating: false,
      entityRelatingSuccess: unrelate ? false : true,
      // an Undo reverts the deal's links only when this relate wrote them
      newAssociations: unrelate
        ? undefined
        : { ...state.newAssociations, deal: deal ? state.newAssociations?.deal : undefined },
    })
  )
  .handleAction(relateUnrelateEntities.failure, (state) => ({
    ...state,
    entityRelating: false,
    entityRelatingSuccess: false,
  }));

export * from "./selectors";
export type CreateEditActivityModalActions = Actions;

export default createEditActivityModal;
